import { useTranslation } from 'react-i18next';
import { useUserTimezone } from '@/hooks/useUserTimezone';

export function useRelativeTime() {
  const userTimezone = useUserTimezone();
  const { t } = useTranslation('common', {
    keyPrefix: 'relativeTime',
  });

  const getRelativeTime = (date: Date): string => {
    const now = new Date();

    // Convert both dates to user's timezone for accurate comparison
    const userDate = new Date(date.toLocaleString('en-US', { timeZone: userTimezone }));
    const userNow = new Date(now.toLocaleString('en-US', { timeZone: userTimezone }));

    const diffInMinutes = Math.floor((userNow.getTime() - userDate.getTime()) / (1000 * 60));
    const isFuture = diffInMinutes < 0;
    const absDiffInMinutes = Math.abs(diffInMinutes);

    // Handle future dates
    if (isFuture) {
      if (absDiffInMinutes < 1) {
        return t('justNow');
      }
      if (absDiffInMinutes < 60) {
        return t('in.minute', { count: absDiffInMinutes });
      }
      if (absDiffInMinutes < 120) {
        return t('in.hour', { count: 1 });
      }

      const diffInHours = Math.floor(absDiffInMinutes / 60);
      if (diffInHours < 24) {
        return t('in.hour', { count: diffInHours });
      }

      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) {
        return t('in.day', { count: diffInDays });
      }

      const diffInWeeks = Math.floor(diffInDays / 7);
      if (diffInWeeks < 4) {
        return t('in.week', { count: diffInWeeks });
      }

      const diffInMonths =
        (userNow.getFullYear() - userDate.getFullYear()) * 12 +
        (userNow.getMonth() - userDate.getMonth());
      if (Math.abs(diffInMonths) < 12) {
        return t('in.month', { count: Math.abs(diffInMonths) });
      }

      const diffInYears = Math.abs(userNow.getFullYear() - userDate.getFullYear());
      return t('in.year', { count: diffInYears });
    }

    // Handle past dates
    if (absDiffInMinutes < 1) {
      return t('justNow');
    }
    if (absDiffInMinutes < 60) {
      return t('ago.minute', { count: absDiffInMinutes });
    }
    if (absDiffInMinutes < 120) {
      return t('ago.hour', { count: 1 });
    }

    const diffInHours = Math.floor(absDiffInMinutes / 60);
    if (diffInHours < 24) {
      return t('ago.hour', { count: diffInHours });
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return t('ago.day', { count: diffInDays });
    }

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
      return t('ago.week', { count: diffInWeeks });
    }

    const diffInMonths =
      (userNow.getFullYear() - userDate.getFullYear()) * 12 +
      (userNow.getMonth() - userDate.getMonth());
    if (Math.abs(diffInMonths) < 12) {
      return t('ago.month', { count: Math.abs(diffInMonths) });
    }

    const diffInYears = Math.abs(userNow.getFullYear() - userDate.getFullYear());
    return t('ago.year', { count: diffInYears });
  };

  return { getRelativeTime };
}
